import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'
import calendar from 'dayjs/plugin/calendar'
import 'dayjs/locale/de'
import 'dayjs/locale/en'
import { DATE_TIME, DAY, DURATION, MONTH, TIME, YEAR } from '@/constants/date.constants.js'
import { i18n } from '@/i18n/index.js'

dayjs.extend(duration)
dayjs.extend(relativeTime)
dayjs.extend(calendar)

dayjs.locale('de')

const FORMATS_TO_DISPLAY = {
  time: 'HH:mm',
  duration: '',
  datetime: 'DD MMM YYYY HH:mm',
  date: 'DD MMM YYYY',
  day: 'DD MMM YYYY',
  date_with_day_of_week: 'DD MMM YYYY, dd',
  day_month: 'DD MMM',
  month: 'MMM YYYY',
  fullMonth: 'MMMM YYYY',
  year: 'YYYY'
}

export const SYSTEM_DATE_FORMAT = 'YYYY-MM-DD'
export const SYSTEM_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm'

export default {
  install(app) {
    app.config.globalProperties.$dayjs = Object.assign(dayjs, {
      now() {
        return dayjs()
      },
      toDisplay(value, type = DAY) {
        if (!value) return value

        if (type === TIME && value.length === 5) {
          return value
        }

        if (type === DURATION) {
          return this.durationToDisplay(this.durationFromBackend(value))
        }

        const format = this.displayFormat(type)

        const locale = i18n.global?.locale?.value ?? i18n.global?.locale

        if (type === MONTH) {
          return dayjs(value).locale(locale).format(format)
        }

        return dayjs(this._toDayJs(value)).locale(locale).format(format)
      },
      toSystem(value, type = DAY) {
        if (!value) return value
        if (type === DURATION) {
          return this._createSystemDurationFromTimeString(value)
        }
        if ([YEAR, TIME].includes(type)) return value
        const format = this.displayFormat(type)

        if (type === MONTH) {
          return dayjs(value, format, 'en').format(SYSTEM_DATE_FORMAT)
        }

        value = this._toDayJs(value)

        const systemFormat =
          type === DATE_TIME ? SYSTEM_DATE_TIME_FORMAT : SYSTEM_DATE_FORMAT
        return dayjs(value, format).format(systemFormat)
      },
      durationToDisplay(duration, format = 'short') {
        const periods = ['month', 'week', 'day', 'hour', 'minute']
        let result = ''
        const separator = format === 'short' ? '' : ' '
        for (const period of periods) {
          const value = duration.get(period)
          if (!value) continue
          const text = i18n.global.t(
            `date.durations.${format}.${period}`,
            value
          )
          result += `${value}${separator}${text} `
        }

        return result.trim()
      },
      durationFromBackend(value) {
        if (!value) return value

        if (typeof value === 'object' && !dayjs.isDayjs(value)) {
          const allowed = ['years', 'months', 'days', 'hours', 'minutes', 'seconds']
          const cleaned = Object.fromEntries(
            Object.entries(value).filter(([key]) => allowed.includes(key))
          )

          return dayjs.duration(cleaned)
        }

        return dayjs.duration(value)
      },
      age(value) {
        const birthDate = dayjs(value)
        const years = dayjs().diff(birthDate, 'year')
        return `${years} ${i18n.global.t('date.durations.full.year', { count: years })}`
      },
      displayFormat(type = DAY) {
        return FORMATS_TO_DISPLAY[type]
      },
      _createSystemDurationFromTimeString(time) {
        const [hours, minutes] = time.split(':')
        return { hours, minutes }
      },
      _toDayJs(value) {
        if (dayjs.isDayjs(value)) {
          return value
        }

        return dayjs(value)
      },
      durationFromSeconds(seconds) {
        return dayjs.duration({ seconds })
      },
      calendarDateTime(value) {
        const { time, datetime } = FORMATS_TO_DISPLAY
        return dayjs(value).calendar(null, {
          sameDay: time,
          nextDay: '[' + i18n.global.t('date.tomorrow') + '] ' + time,
          nextWeek: datetime,
          lastDay: '[' + i18n.global.t('date.yesterday') + '] ' + time,
          lastWeek: datetime,
          sameElse: datetime
        })
      },
      durationFromTimestamp(value, format = 'short') {
        if (!value) return value

        const duration = dayjs.duration(value)
        return this.durationToDisplay(duration, format)
      }
    })
  }
}

export const $dayjs = dayjs
